if (!customElements.get('wallify-frame-preview')) {
  const galleryFilters = new WeakMap();

  function frameGalleryFilter(gallery) {
    if (!gallery) return null;
    if (galleryFilters.has(gallery)) return galleryFilters.get(gallery);

    const root = gallery.closest('.product');
    if (!root) return null;
    const entries = new Map();
    let pending = null;
    let syncing = false;
    let disposed = false;
    let previousFramed = null;
    let previousPoster = null;
    let posterSelectionRequested = false;
    let selectionToken = 0;
    let lastFramePosition = 2;
    const pickerGroups = () => Array.from(root.querySelector('variant-selects')?.children || []).filter(node => node.classList.contains('product-form__input'));
    const snapshot = () => pickerGroups().map(group => selectedPosition(group));
    const setPosition = (group, index) => {
      const select = group?.querySelector('select');
      if (select) select.selectedIndex = index;
      else Array.from(group?.querySelectorAll('input[type="radio"]') || []).forEach((input, position) => { input.checked = position === index; });
    };
    let committedSelection;
    const slides = list => Array.from(list?.children || []).filter(node => node.classList.contains('splide__slide'));
    const dynamic = slide => !!slide.querySelector('wallify-set-frame-preview, wallify-frame-preview:not([data-lens-clone])');
    const selectedPosition = group => {
      const select = group?.querySelector('select');
      return select ? select.selectedIndex : Array.from(group?.querySelectorAll('input[type="radio"]') || []).findIndex(input => input.checked);
    };
    const schedule = () => {
      if (pending !== null || disposed) return;
      pending = requestAnimationFrame(() => { pending = null; sync(); });
    };
    const sync = () => {
      if (syncing || disposed || !gallery.isConnected) return;
      const mainList = gallery.main?.Components?.Elements?.list;
      const thumbList = gallery.thumbnails?.Components?.Elements?.list;
      const picker = root.querySelector('variant-selects');
      if (!mainList || !thumbList || !picker) return;
      const groups = Array.from(picker.children).filter(node => node.classList.contains('product-form__input'));
      // The catalog's first material is unframed; a stale color must not expose a frame.
      const framed = selectedPosition(groups[0]) > 0 && selectedPosition(groups[2]) > 0;
      const poster = selectedPosition(groups[0]) === 0;
      const materialBecameFramed = framed && previousFramed === false;
      const materialBecamePoster = poster && previousPoster === false && posterSelectionRequested;
      previousFramed = framed;
      previousPoster = poster;
      posterSelectionRequested = false;
      const originals = gallery.originalSlides?.length ? gallery.originalSlides : slides(mainList);
      const originalThumbs = gallery.originalThumbnails?.length ? gallery.originalThumbnails : slides(thumbList);
      originals.forEach((slide, index) => {
        const isDynamic = dynamic(slide);
        const isPoster = !isDynamic && slide.dataset.wallifyPosterPreview === 'true';
        if ((!isDynamic && !isPoster) || entries.has(slide.dataset.mediaId)) return;
        entries.set(slide.dataset.mediaId, {
          slide, index, poster: isPoster,
          thumb: originalThumbs.find(node => node.dataset.mediaId === slide.dataset.mediaId),
          unframedId: slide.querySelector('wallify-frame-preview[data-unframed-media-id]')?.dataset.unframedMediaId ||
            slide.querySelector('wallify-set-frame-preview')?.dataset.firstMedia
        });
      });
      if (!entries.size) return;
      const active = gallery.main.Components.Elements.slides[gallery.main.index] || mainList.querySelector('.is-active');
      let mediaId = active?.dataset.mediaId;
      let changed = false;
      syncing = true;
      try {
        entries.forEach(entry => {
          const attached = entry.slide.parentElement === mainList;
          const designAvailable = entry.slide.dataset.wallifyDesignAvailable !== 'false';
          if ((entry.poster ? !poster : !framed) || !designAvailable) {
            if (attached) {
              if (mediaId === entry.slide.dataset.mediaId) mediaId = entry.poster || !designAvailable ? entry.slide.dataset.wallifyDesignFallbackMediaId || entry.unframedId : entry.unframedId;
              entry.slide.remove(); changed = true;
            }
            if (entry.thumb?.parentElement === thumbList) { entry.thumb.remove(); changed = true; }
          } else {
            if (!attached) {
              const next = originals.slice(entry.index + 1).find(node => node.parentElement === mainList);
              mainList.insertBefore(entry.slide, next || null); changed = true;
              // A newly reattached frame preview follows the user's material selection.
              if (entry.poster ? materialBecamePoster : !entry.slide.hasAttribute('data-wallify-design-available') || materialBecameFramed) mediaId = entry.slide.dataset.mediaId;
            }
            if (entry.thumb && entry.thumb.parentElement !== thumbList) {
              const index = originalThumbs.indexOf(entry.thumb);
              const next = originalThumbs.slice(index + 1).find(node => node.parentElement === thumbList);
              thumbList.insertBefore(entry.thumb, next || null); changed = true;
            }
          }
        });
        if (!changed) return;
        gallery.main.refresh();
        gallery.thumbnails.refresh();
        const visible = gallery.main.Components.Elements.slides;
        let index = visible.findIndex(node => node.dataset.mediaId === mediaId);
        if (index < 0) index = 0;
        if (visible.length) { gallery.main.go(index); gallery.thumbnails.go(index); }
        gallery.updateSlideCounter?.(visible.length ? index : -1);
        gallery.updateThumbnailARIA?.();
      } finally { syncing = false; }
    };
    const handleChange = event => {
      const picker = event.target.closest?.('variant-selects');
      if (picker?.closest('.product') !== root) return;
      const token = ++selectionToken;
      const groups = pickerGroups();
      const material = groups[0];
      if (material?.contains(event.target)) {
        posterSelectionRequested = selectedPosition(material) === 0;
        if (selectedPosition(material) > 0 && selectedPosition(groups[2]) === 0) setPosition(groups[2], lastFramePosition);
      }
      const desired = snapshot();
      if (desired[2] > 0) lastFramePosition = desired[2];
      const personalization = root.querySelector('wallify-personalization-order');
      const previews = Array.from(entries.values()).filter(entry => !entry.poster).flatMap(entry => Array.from(entry.slide.querySelectorAll('wallify-frame-preview')));
      const preparations = desired[0] > 0 ? [
        ...(personalization?.prepareFramedSelection ? [personalization.prepareFramedSelection()] : []),
        ...previews.map(preview => preview.preparePreviewArtwork(['none', 'white', 'natural-wood', 'dark-wood', 'black'][desired[2]]))
      ] : [];
      if (preparations.some(preparation => preparation.status !== 'ready')) {
        event.stopImmediatePropagation();
        committedSelection?.forEach((position, index) => setPosition(groups[index], position));
        Promise.all(preparations.map(preparation => preparation.promise)).then(results => {
          if (disposed || token !== selectionToken || !root.isConnected || !results.every(Boolean)) return;
          desired.forEach((position, index) => setPosition(groups[index], position));
          event.target.dispatchEvent(new Event('change', { bubbles: true }));
        });
        return;
      }
      committedSelection = desired;
      previews.forEach(preview => preview.updateFrame({ updateGallery: false }));
      sync();
      schedule();
    };
    committedSelection = snapshot();
    const observer = new MutationObserver(schedule);
    observer.observe(root, { childList: true, subtree: true });
    root.addEventListener('change', handleChange, true);
    root.addEventListener('wallify:premium-change', schedule, true);
    const handleDesignChange = () => { sync(); schedule(); };
    root.addEventListener('wallify:design-preview-change', handleDesignChange, true);
    const controller = {
      gallery, sync,
      dispose() {
        disposed = true; observer.disconnect();
        root.removeEventListener('change', handleChange, true);
        root.removeEventListener('wallify:premium-change', schedule, true);
        root.removeEventListener('wallify:design-preview-change', handleDesignChange, true);
        if (pending !== null) cancelAnimationFrame(pending);
        galleryFilters.delete(gallery);
      }
    };
    galleryFilters.set(gallery, controller);
    customElements.whenDefined('gallery-carousel').then(schedule);
    schedule();
    return controller;
  }

  class WallifyFramePreview extends HTMLElement {
    static preloadedSources = new Set();
    static artworkDecodes = new WeakMap();
    static frameTextures = new Map();

    constructor() {
      super();
      this.handleChange = this.handleChange.bind(this);
      this.pendingFrame = null;
    }

    connectedCallback() {
      if (this.hasAttribute('data-lens-clone')) return;
      this.productRoot = this.closest('.product');
      this.preparePreviewArtwork();
      this.galleryFilter = frameGalleryFilter(this.closest('gallery-carousel'));
      this.productRoot?.addEventListener('change', this.handleChange, true);

      queueMicrotask(() => {
        if (!this.isConnected) return;

        const preserveInitialMedia =
          this.closest('gallery-carousel')?.dataset.wallifyPreserveInitialMedia === 'true' || this.closest('[data-wallify-design-available]') !== null;

        this.updateFrame({ updateGallery: !preserveInitialMedia });

        this.scheduleTextureWarmup();
      });
    }

    scheduleTextureWarmup() {
      const warmup = () => {
        this.textureWarmup = null;
        if (!this.isConnected) return;
        ['srcWhite', 'srcNaturalWood', 'srcDarkWood', 'srcBlack'].forEach(key => {
          const source = this.dataset[key];
          if (!source || WallifyFramePreview.preloadedSources.has(source)) return;
          WallifyFramePreview.preloadedSources.add(source);
          const image = new Image();
          image.src = source;
        });
      };
      this.textureWarmupIsIdle = typeof window.requestIdleCallback === 'function';
      this.textureWarmup = this.textureWarmupIsIdle
        ? window.requestIdleCallback(warmup, { timeout: 3000 })
        : window.setTimeout(warmup, 1500);
    }

    preparePreviewArtwork(frameKey = this.dataset.frame) {
      const images = Array.from(this.querySelectorAll('img.wallify-frame-preview__artwork'));
      const decodes = images.map(image => {
        image.loading = 'eager';
        const source = image.currentSrc || image.src;
        const existing = WallifyFramePreview.artworkDecodes.get(image);
        if (existing?.source === source) return existing;
        const decode = { source, status: 'pending' };
        decode.promise = (typeof image.decode === 'function' ? image.decode() : Promise.resolve()).then(() => {
          const ready = image.complete && image.naturalWidth > 0 && (image.currentSrc || image.src) === source;
          decode.status = ready ? 'ready' : 'failed';
          return ready;
        }, () => { decode.status = 'failed'; return false; });
        WallifyFramePreview.artworkDecodes.set(image, decode);
        return decode;
      });
      const textureKey = { white: 'srcWhite', 'natural-wood': 'srcNaturalWood', 'dark-wood': 'srcDarkWood', black: 'srcBlack' }[frameKey];
      const textureSource = this.dataset[textureKey];
      if (textureSource) {
        let texture = WallifyFramePreview.frameTextures.get(textureSource);
        if (!texture) {
          texture = { status: 'pending' };
          const image = new Image();
          image.src = textureSource;
          texture.promise = image.decode().then(() => {
            texture.status = image.naturalWidth > 0 ? 'ready' : 'failed';
            return texture.status === 'ready';
          }, () => { texture.status = 'failed'; return false; });
          WallifyFramePreview.frameTextures.set(textureSource, texture);
        }
        decodes.push(texture);
      }
      return {
        status: decodes.every(decode => decode.status === 'ready') ? 'ready' : 'pending',
        promise: Promise.all(decodes.map(decode => decode.promise)).then(results => results.every(Boolean))
      };
    }

    disconnectedCallback() {
      if (this.textureWarmup != null) {
        if (this.textureWarmupIsIdle) window.cancelIdleCallback(this.textureWarmup);
        else window.clearTimeout(this.textureWarmup);
        this.textureWarmup = null;
      }
      this.productRoot?.removeEventListener('change', this.handleChange, true);
      this.productRoot = null;
      if (this.galleryFilter && !this.galleryFilter.gallery.isConnected) this.galleryFilter.dispose();
      if (this.pendingFrame !== null) cancelAnimationFrame(this.pendingFrame);
      this.pendingFrame = null;
    }

    handleChange(event) {
      const picker = event.target.closest?.('variant-selects');

      if (!picker || picker.closest('.product') !== this.productRoot) return;
      if (this.pendingFrame !== null) return;

      this.pendingFrame = requestAnimationFrame(() => {
        this.pendingFrame = null;
        if (this.isConnected) this.updateFrame();
      });
    }

    updateFrame({ updateGallery = true } = {}) {
      const picker = this.productRoot?.querySelector('variant-selects');

      if (!picker) return;

      const containers = Array.from(picker.children).filter((child) =>
        child.classList.contains('product-form__input')
      );

      const container = containers[Number(this.dataset.frameOption)];

      if (!container) return;

      const select = container.querySelector('select');
      const radios = Array.from(
        container.querySelectorAll('input[type="radio"]')
      );

      const position = select
        ? select.selectedIndex
        : radios.findIndex((radio) => radio.checked);

      const selectedValue = (select ? select.selectedOptions[0]?.value : radios[position]?.value)?.toLowerCase().replace(/[_\s]+/g, '-') || '';
      const keys = {"no-frame": "none", "senza-cornice": "none", "sans-cadre": "none", "sin-marco": "none", "ohne-rahmen": "none", "sem-moldura": "none", "zonder-lijst": "none", "uden-ramme": "none", "utan-ram": "none", "ilman-kehystä": "none", "uten-ramme": "none", "bez-ramy": "none", "white": "white", "bianco": "white", "blanc": "white", "blanco": "white", "weiß": "white", "branco": "white", "wit": "white", "hvid": "white", "vit": "white", "valkoinen": "white", "hvit": "white", "biały": "white", "natural-wood": "natural-wood", "wood-frame": "natural-wood", "legno-naturale": "natural-wood", "naturale": "natural-wood", "bois-naturel": "natural-wood", "madera-natural": "natural-wood", "naturholz": "natural-wood", "madeira-natural": "natural-wood", "natuurlijk-hout": "natural-wood", "naturtræ": "natural-wood", "naturträ": "natural-wood", "luonnonpuu": "natural-wood", "naturtre": "natural-wood", "naturalne-drewno": "natural-wood", "dark-wood": "dark-wood", "legno-scuro": "dark-wood", "scuro": "dark-wood", "bois-foncé": "dark-wood", "madera-oscura": "dark-wood", "dunkles-holz": "dark-wood", "madeira-escura": "dark-wood", "donker-hout": "dark-wood", "mørkt-træ": "dark-wood", "mörkt-trä": "dark-wood", "tumma-puu": "dark-wood", "mørkt-tre": "dark-wood", "ciemne-drewno": "dark-wood", "black": "black", "nero": "black", "noir": "black", "negro": "black", "schwarz": "black", "preto": "black", "zwart": "black", "sort": "black", "svart": "black", "musta": "black", "czarny": "black"};
      const key = keys[selectedValue] || ['none', 'white', 'natural-wood', 'dark-wood', 'black'][position];

      if (!key) return;

      if (key === 'none') {
        this.dataset.frame = 'none';
        this.galleryFilter?.sync();
        if (updateGallery) this.updateCarousel(picker, key);
        return;
      }

      this.dataset.frame = key;
      this.galleryFilter?.sync();

      const selected = select
        ? select.selectedOptions[0]
        : radios[position];

      const label =
        selected?.dataset.wallifyDisplayValue ||
        selected?.value ||
        '';

      this.setAttribute(
        'aria-label',
        [this.dataset.productTitle, label].filter(Boolean).join(' — ')
      );

      if (updateGallery) this.updateCarousel(picker, key);
    }

    updateCarousel(picker, key) {
      if (
        this.classList.contains(
          'wallify-frame-preview--thumbnail'
        )
      ) {
        return;
      }

      const gallery = this.closest('gallery-carousel');
      const ownMediaId =
        this.closest('[data-media-id]')?.dataset.mediaId;

      if (
        !gallery ||
        !ownMediaId ||
        typeof gallery.updateCarouselImages !== 'function'
      ) {
        return;
      }

      if (key !== 'none') {
        gallery.updateCarouselImages(ownMediaId);
        return;
      }

      const unframedMediaId = this.dataset.unframedMediaId;

      if (
        unframedMediaId &&
        unframedMediaId !== ownMediaId
      ) {
        gallery.updateCarouselImages(unframedMediaId);
        return;
      }

      const containers = Array.from(picker.children).filter((child) =>
        child.classList.contains('product-form__input')
      );

      const selectedOptions = containers.map((container) => {
        const select = container.querySelector('select');

        if (select) return select.value;

        return Array.from(
          container.querySelectorAll('input[type="radio"]')
        ).find((radio) => radio.checked)?.value;
      });

      let variant = picker.currentVariant;

      if (
        !variant &&
        typeof picker.getVariantData === 'function'
      ) {
        variant = picker
          .getVariantData()
          .find((candidate) =>
            candidate.options?.every(
              (option, index) =>
                option === selectedOptions[index]
            )
          );
      }

      const variantMediaId = variant?.featured_media?.id;

      if (
        variantMediaId &&
        String(variantMediaId) !== ownMediaId
      ) {
        gallery.updateCarouselImages(variantMediaId);
        return;
      }

      const mainCarousel = gallery.querySelector(
        '[id^="Main-Carousel"]'
      );

      const fallbackSlide = Array.from(
        mainCarousel?.querySelectorAll(
          '.splide__slide[data-media-id]'
        ) || []
      ).find(
        (slide) => slide.dataset.mediaId !== ownMediaId
      );

      if (fallbackSlide?.dataset.mediaId) {
        gallery.updateCarouselImages(
          fallbackSlide.dataset.mediaId
        );
      }
    }
  }

  customElements.define(
    'wallify-frame-preview',
    WallifyFramePreview
  );
}


